<template>
  <section class="admin-page">
    <div class="admin-scroll">
      <header class="admin-header">
        <div>
          <span class="admin-label">标签管理</span>
          <h2>管理文章标签</h2>
          <p class="admin-desc">管理博客文章的标签，为内容添加关键词标记。</p>
        </div>
        <button class="admin-btn admin-btn-primary" type="button" @click="openCreateModal">
          <span class="admin-btn-icon">+</span>
          新建标签
        </button>
      </header>

      <section class="admin-panel">
        <div class="admin-toolbar">
          <label class="admin-search">
            <svg class="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" class="admin-search-input" placeholder="搜索标签名称" v-model="searchKeyword" @keyup.enter="handleSearch" />
          </label>
        </div>

        <div class="admin-list">
          <div v-if="loading" class="admin-loading">
            <div class="admin-spinner"></div>
            <span>加载中...</span>
          </div>
          <article
            v-for="tag in filteredTags"
            :key="tag.id"
            class="admin-list-item"
          >
            <div class="admin-list-body">
              <h3 class="admin-list-title">{{ tag.name }}</h3>
              <div class="admin-list-meta">
                <span class="admin-meta-tag">{{ formatDate(tag.created_at) }}</span>
              </div>
            </div>
            <div class="admin-list-actions">
              <button class="admin-btn admin-btn-ghost" type="button" @click="editTag(tag)">编辑</button>
              <button class="admin-btn admin-btn-ghost" type="button" style="color: var(--danger);" @click="handleDeleteTag(tag.id)">删除</button>
            </div>
          </article>

          <div v-if="filteredTags.length === 0" class="admin-empty">
            <h3>暂无标签</h3>
            <p>点击上方「新建标签」按钮创建第一个标签。</p>
            <button class="admin-btn admin-btn-primary" type="button" @click="openCreateModal">新建标签</button>
          </div>
        </div>

        <AdminPagination
          :current-page="currentPage"
          :total-pages="totalPages"
          :on-page-change="goToPage"
        />
      </section>
    </div>

    <!-- 模态框 -->
    <div v-if="showModal" class="admin-modal-overlay" @wheel.prevent>
      <div :class="['admin-modal', { 'admin-modal-visible': modalVisible }]" @wheel.stop>
        <div class="admin-modal-header">
          <h2>{{ isEditing ? '编辑标签' : '新建标签' }}</h2>
          <button class="admin-modal-close" type="button" aria-label="关闭" @click="closeModal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="admin-modal-body">
          <div class="admin-field">
            <label class="admin-field-label required" for="tag-name">标签名称</label>
            <input id="tag-name" v-model.trim="tagName" class="admin-input" type="text" placeholder="请输入标签名称" />
          </div>
        </div>

        <div class="admin-modal-footer">
          <button class="admin-btn admin-btn-secondary" type="button" @click="closeModal">取消</button>
          <button class="admin-btn admin-btn-primary" type="button" :disabled="isSubmitting" @click="saveTag">
            <span v-if="isSubmitting" class="admin-spinner"></span>
            {{ isSubmitting ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { getAdminTags, createTag, updateTag, deleteTag as deleteTagApi } from '@/services/tagService'
import { useNotify } from '@/composables/useNotify'
import AdminPagination from '../components/admin/AdminPagination.vue'

const { notify, confirmDialog } = useNotify()

const tags = ref([])
const loading = ref(true)
const searchKeyword = ref('')
const showModal = ref(false)
const modalVisible = ref(false)
const isEditing = ref(false)
const currentTagId = ref(null)
const tagName = ref('')
const isSubmitting = ref(false)
const currentPage = ref(1)
const totalPages = ref(1)
const totalTags = ref(0)
const PER_PAGE = 10

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

watch(showModal, (visible) => {
  if (visible) {
    const bodyScrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (bodyScrollbarWidth > 0) {
      document.body.style.paddingRight = `${bodyScrollbarWidth}px`
    }
  } else {
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }
})

// 搜索已在后端完成，直接使用 tags
const filteredTags = computed(() => tags.value)

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchTags()
}

const fetchTags = async () => {
  loading.value = true
  try {
    const keyword = searchKeyword.value.trim()
    const res = await getAdminTags(currentPage.value, PER_PAGE, keyword)
    const data = res.data
    tags.value = data?.list || []
    totalTags.value = data?.total || 0
    totalPages.value = data?.total_page || Math.ceil(totalTags.value / PER_PAGE) || 1
  } catch (e) {
    notify.error(e.message || '获取标签列表失败')
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  isEditing.value = false
  currentTagId.value = null
  tagName.value = ''
  showModal.value = true
  requestAnimationFrame(() => { modalVisible.value = true })
}

const closeModal = () => {
  modalVisible.value = false
  setTimeout(() => { showModal.value = false }, 250)
}

const editTag = (tag) => {
  isEditing.value = true
  currentTagId.value = tag.id
  tagName.value = tag.name
  showModal.value = true
  requestAnimationFrame(() => { modalVisible.value = true })
}

const saveTag = async () => {
  if (!tagName.value) { notify.warning('请输入标签名称。'); return }
  isSubmitting.value = true
  try {
    if (isEditing.value) {
      await updateTag(currentTagId.value, { name: tagName.value })
    } else {
      await createTag({ name: tagName.value })
    }
    await fetchTags()
    closeModal()
    notify.success('标签保存成功')
  } catch (e) {
    notify.error(`保存失败：${e.message}`)
  } finally {
    isSubmitting.value = false
  }
}

const handleDeleteTag = async (id) => {
  if (!await confirmDialog('确定要删除这个标签吗？')) return
  try {
    await deleteTagApi(id)
    await fetchTags()
    notify.success('标签删除成功')
  } catch (e) {
    notify.error(`删除失败：${e.message}`)
  }
}

// 回车搜索
const handleSearch = () => {
  currentPage.value = 1
  fetchTags()
}

onMounted(() => {
  fetchTags()
})
</script>

<style scoped>
/* 覆盖全局模态框样式，让标签编辑弹窗更紧凑 */
:deep(.admin-modal) {
  min-width: 400px;
  min-height: unset;
  width: min(450px, 96vw);
}

:deep(.admin-modal-header) {
  padding: 16px 20px;
}

:deep(.admin-modal-body) {
  padding: 16px 20px;
}

:deep(.admin-modal-footer) {
  padding: 12px 20px;
}

</style>
